import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Button from "./Button";

import { Tabs, Tab } from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import useFontFamilies from "../../../hooks/useFontFamilies";

export default function GlobalSettingForm() {
  const fontOptions = useFontFamilies('option');
  const navigate = useNavigate();

  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadedFileInfo, setUploadedFileInfo] = useState(null);
  const [buttonColor, setButtonColor] = useState("");

  // Next button file state
  const [nextButtonFile, setNextButtonFile] = useState(null);
  const [nextButtonUploadStatus, setNextButtonUploadStatus] = useState("");

  const { userId } = useParams(); // Get the optional client ID from the route

  const [settings, setSettings] = useState({
    userId: null,

    globalSettings: {
      buttonText: "",
      cornerRadius: 5,
      height: 50,
      width: 150,
      outlineColor: "#000000",
      outlineThickness: 1,
      fillColor: "#ffffff",
      font: "Arial",
      fontColor: "#000000",
      fontSize: 16,
      shadow: {
        angle: 125,
        size: 10,
        distance: 15,
        alpha: 0.2,
        color: "#000000", // Bright orange
      },
      highlight: {
        borderColor: "#8f8f8f",
        fillColor: "#000000",
        fontColor: "#ffffff",
      },
      clickSound: "",
      clickSoundFilename: null,
      clickSoundVolume: 1,
      textAlign: "center",
      marginBottom: 2,
      lineHeight: 1,
      padding: 10,
      nextButtonFileName: "",
      nextButtonFileUrl: "",
      nextButtonWidth: 0,
    },
  });

  // Function to go back to the previous page
  const goBack = () => {
    navigate(-1); // This will take the user back to the previous page in the browser history
  };

  useEffect(() => {
    if (userId) {
      fetch(`/api/get-user-global/${userId}`)
        .then((res) => res.json())
        .then((data) => {
          console.log("Loaded data from server:", data);
          if (!data.error) {
            setSettings((prevSettings) => ({
              ...prevSettings,
              globalSettings: {
                ...prevSettings?.globalSettings,
                ...data.result?.globalSettings, // Merge globalSettings as well
              },
            }));

            console.log("Next button data loaded:", {
              nextButtonFileName: data.result?.globalSettings?.nextButtonFileName,
              nextButtonFileUrl: data.result?.globalSettings?.nextButtonFileUrl,
              nextButtonWidth: data.result?.globalSettings?.nextButtonWidth,
            });

            setButtonColor(data?.site_brand_color);
          }
        })
        .catch((err) => {
          console.error("Error loading button settings:", err);
          console.log("Button not found, creating new settings.");
        });
    }
  }, [userId]);

  const handleChange = (key, value) => {
    // Determine whether to update global or custom settings
    const basePath = "globalSettings";
    const fullPath = `${basePath}.${key}`;
    const keys = fullPath.split("."); // Split the path into parts
    const newSettings = { ...settings };

    keys.reduce((obj, currentKey, index) => {
      if (index === keys.length - 1) {
        // If it's the last key, update the value
        obj[currentKey] = value;
      } else {
        // Otherwise, ensure the object exists before traversing
        obj[currentKey] = { ...obj[currentKey] };
      }
      return obj[currentKey];
    }, newSettings);

    setSettings(newSettings);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    settings.userId = userId;

    console.log("settings being sent:", settings);
    console.log("nextButtonFileName:", settings?.globalSettings?.nextButtonFileName);
    console.log("nextButtonFileUrl:", settings?.globalSettings?.nextButtonFileUrl);
    console.log("nextButtonWidth:", settings?.globalSettings?.nextButtonWidth);

    try {
      const response = await fetch(`/api/save-user-global-settings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });

      const result = await response.json();
      console.log("Server response:", result);

      if (response.ok) {
        alert("Button settings saved successfully!");
      } else {
        console.error("Failed to save button settings:", result.message || result.error);
        alert("Failed to save settings: " + (result.message || result.error));
      }
    } catch (error) {
      console.error("Error saving button settings:", error);
      alert("Error saving settings: " + error.message);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setUploadedFileInfo(null); // Reset file info on new file selection
  };

  const handleUpload = async () => {
    if (!file) return;

    try {
      // Step 1: Get Pre-signed URL from the Backend
      const response = await axios.get("/generate-signed-url", {
        params: {
          fileName: file.name,
          fileType: file.type,
        },
      });

      const { url } = response.data;

      // Step 2: Upload the File to S3
      await axios.put(url, file, {
        headers: {
          "Content-Type": file.type,
        },
      });

      setUploadStatus("File uploaded successfully!");

      // Step 3: Save Uploaded File Information
      const fileUrl = url.split("?")[0]; // Remove query params to get the actual file URL
      setUploadedFileInfo({
        filename: file.name,
        url: fileUrl,
        type: file.type,
      });

      // Step 4: Update Click Sound in Settings
      setSettings((prevSettings) => {
        console.log(prevSettings); // Check prevSettings here
        return {
          ...prevSettings,
          globalSettings: {
            ...prevSettings?.globalSettings,
            clickSound: fileUrl,
            clickSoundFilename: file.name,
          },
        };
      });
    } catch (error) {
      console.error("Upload failed:", error);
      setUploadStatus("Upload failed. Please try again.");
    }
  };

  // Next Button Upload Handler
  const handleNextButtonFileChange = (e) => {
    setNextButtonFile(e.target.files[0]);
  };

  const handleNextButtonUpload = async () => {
    if (!nextButtonFile) return;

    try {
      // Step 1: Get Pre-signed URL from the Backend
      const response = await axios.get("/generate-signed-url", {
        params: {
          fileName: nextButtonFile.name,
          fileType: nextButtonFile.type,
        },
      });

      const { url } = response.data;

      // Step 2: Upload the File to S3
      await axios.put(url, nextButtonFile, {
        headers: {
          "Content-Type": nextButtonFile.type,
        },
      });

      setNextButtonUploadStatus("Next button uploaded successfully!");

      // Step 3: Save Uploaded File Information
      const fileUrl = url.split("?")[0]; // Remove query params to get the actual file URL

      // Step 4: Update Next Button in Settings
      setSettings((prevSettings) => {
        return {
          ...prevSettings,
          globalSettings: {
            ...prevSettings?.globalSettings,
            nextButtonFileUrl: fileUrl,
            nextButtonFileName: nextButtonFile.name,
          },
        };
      });

      setNextButtonFile(null); // Reset file input
    } catch (error) {
      console.error("Next button upload failed:", error);
      setNextButtonUploadStatus("Upload failed. Please try again.");
    }
  };

  const [value, setValue] = useState(2);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <div className="main-container">
      <div className="pd-ltr-20 xs-pd-20-10">
        <div className="min-height-200px dashboard-container">
          <div className="page-header">
            <div className="row">
              <div className="col-md-6 col-sm-12">
                <div className="title">
                  <h4>Global Settings</h4>
                </div>
              </div>

              {/* <div className="col-md-6 col-sm-12">
                      <button type='button' onClick={goBack} style={{float:"right",backgroundColor:buttonColor}} className="btn btn-success">
                         {`<< Back`}
                      </button>
                      </div> */}

              <div className="col-sm-12 text-end">
                <button
                  className="btn btn-outline-secondary btn-back-effect"
                  onClick={() => navigate(-1)}
                  style={{
                    "--brand-color": buttonColor,
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                </button>
              </div>
            </div>
          </div>

          <Tabs
            value={value}
            onChange={handleTabChange}
            aria-label="tab navigation"
            sx={{
              "& .MuiTab-root": { minWidth: 100 }, // Ensures consistent width
              "& .Mui-selected": { fontWeight: "bold" }, // Active tab styling
            }}
            style={{ marginBottom: "36px" }}
          >
            <Tab
              onClick={() => navigate(`/view-button-settings/${userId}`)}
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor, // Add your desired hover color here
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label="Menu Settings"
            />

            <Tab
              onClick={() => navigate(`/view-splash-settings/${userId}`)}
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor, // Add your desired hover color here
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label={"Splash Settings"}
            />
            <Tab
              onClick={() => navigate(`/global-settings/${userId}`)}
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor, // Add your desired hover color here
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label={"Global Settings"}
            />

            <Tab
              onClick={() => navigate(`/availablity-settings/${userId}`)}
              sx={{
                "&:hover": {
                  backgroundColor: buttonColor, // Add your desired hover color here
                  color: "#fff",
                  fontWeight: "bold",
                },
              }}
              label={"System Settings"}
            />
          </Tabs>

          <form
            className="tab-wizard wizard-circle wizard vertical"
            onSubmit={handleSubmit}
            style={{ padding: "24px" }}
          >
            <div
              style={{ marginBottom: "23px", textAlign: "right" }}
              className=""
            >
              <div className="right" style={{}}>
                <Button
                  text={"Sample Button"}
                  settings={settings?.globalSettings}
                />
              </div>
            </div>

            {/* Global Settings */}

            <div className="row menu-card2">
              <div className="col-md-4">
                <div className="form-group">
                  <label>Font Line Height:</label>
                  <input
                    type="number"
                    className="form-control"
                    value={
                      settings.enabled
                        ? (settings?.globalSettings?.lineHeight ?? 1)
                        : (settings?.globalSettings?.lineHeight ?? 1)
                    }
                    onChange={(e) =>
                      handleChange("lineHeight", Number(e.target.value))
                    }
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Padding:</label>
                  <input
                    type="number"
                    className="form-control"
                    value={
                      settings.enabled
                        ? (settings?.globalSettings?.padding ?? 10)
                        : (settings?.globalSettings?.padding ?? 10)
                    }
                    onChange={(e) =>
                      handleChange("padding", Number(e.target.value))
                    }
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Margin Bottom:</label>
                  <input
                    type="number"
                    className="form-control"
                    value={
                      settings.enabled
                        ? (settings?.globalSettings?.marginBottom ?? 0)
                        : (settings?.globalSettings?.marginBottom ?? 0)
                    }
                    onChange={(e) =>
                      handleChange("marginBottom", Number(e.target.value))
                    }
                  />
                </div>
              </div>
            </div>

            <div className="row menu-card2">
              <div className="col-md-4">
                <div className="form-group">
                  <label>Corner Radius:</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings?.globalSettings?.cornerRadius ?? 0}
                    onChange={(e) =>
                      handleChange("cornerRadius", Number(e.target.value))
                    }
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Height:</label>
                  <input
                    type="number"
                    value={settings?.globalSettings?.height ?? 0}
                    onChange={(e) =>
                      handleChange("height", Number(e.target.value))
                    }
                    className="form-control"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Width:</label>
                  <input
                    type="number"
                    value={settings?.globalSettings?.width ?? 0}
                    onChange={(e) =>
                      handleChange("width", Number(e.target.value))
                    }
                    className="form-control"
                  />
                </div>
              </div>
            </div>

            {/* Outline */}

            <div className="row menu-card2">
              <div className="col-md-4">
                <div className="form-group">
                  <label
                    className="d-flex align-items-center"
                    style={{ height: "25px" }}
                  >
                    Outline color: (Transparent
                    <input
                      type="checkbox"
                      className="ml-1 mr-1"
                      checked={
                        settings?.globalSettings?.outlineColor === "transparent"
                      }
                      onChange={(e) =>
                        handleChange(
                          "outlineColor",
                          e.target.checked ? "transparent" : "#000000",
                        )
                      }
                    />
                    )
                  </label>

                  <MuiColorInput
                    className="form-control"
                    format="hex"
                    value={settings?.globalSettings?.outlineColor || "#000000"}
                    onChange={(newHex) =>
                      handleChange("outlineColor", newHex?.toUpperCase?.())
                    }
                    fallbackValue="#000000"
                    disabled={
                      settings?.globalSettings?.outlineColor === "transparent"
                    } // Disable when transparent
                    // size="small"
                    isAlphaHidden={true} // This disables the alpha slider
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px !important",
                        height: 53,
                      },
                      "& .MuiOutlinedInput-input": {
                        height: "36px",
                      },
                      "& .MuiColorInput-Button": {
                        width: "49px !important", // Increase width
                        height: "40px !important",
                      },
                      "& .MuiInputAdornment-root .MuiBox-root": {
                        width: 18,
                        height: 18,
                      },
                    }}
                  />

                  {/* <input
                    type="color"
                    disabled={settings?.globalSettings?.outlineColor === "transparent"} // Disable when transparent
                    className="form-control"
                    value={
                      settings?.globalSettings?.outlineColor || "#000000"
                    }
                    onChange={(e) => handleChange('outlineColor', e.target.value)}
                  /> */}
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Outline Thickness:</label>
                  <input
                    type="number"
                    value={settings?.globalSettings?.outlineThickness ?? 0}
                    onChange={(e) =>
                      handleChange("outlineThickness", Number(e.target.value))
                    }
                    className="form-control"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label>Text Alignment (left centre or right):</label>

                  <select
                    id="textAlign"
                    value={settings?.globalSettings?.textAlign || "left"}
                    onChange={(e) => handleChange("textAlign", e.target.value)}
                    className="form-select form-control"
                  >
                    <option value="left">Left</option>
                    <option value="center">Center</option>
                    <option value="right">Right</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Fill Color */}
            <div className="row menu-card2">
              <div className="col-md-3">
                <div className="form-group">
                  <label
                    className="d-flex align-items-center"
                    style={{ height: "25px" }}
                  >
                    Fill color: (Transparent
                    <input
                      type="checkbox"
                      className="ml-1 mr-1"
                      checked={
                        settings?.globalSettings?.fillColor === "transparent"
                      }
                      onChange={(e) =>
                        handleChange(
                          "fillColor",
                          e.target.checked ? "transparent" : "#000000",
                        )
                      }
                    />
                    )
                  </label>
                  <MuiColorInput
                    className="form-control"
                    format="hex"
                    value={settings?.globalSettings?.fillColor || "#000000"}
                    onChange={(newHex) =>
                      handleChange("fillColor", newHex?.toUpperCase?.())
                    }
                    fallbackValue="#000000"
                    disabled={
                      settings?.globalSettings?.fillColor === "transparent"
                    } // Disable when transparent
                    // size="small"
                    isAlphaHidden={true} // This disables the alpha slider
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px !important",
                        height: 53,
                      },
                      "& .MuiOutlinedInput-input": {
                        height: "36px",
                      },
                      "& .MuiColorInput-Button": {
                        width: "49px !important", // Increase width
                        height: "40px !important",
                      },
                      "& .MuiInputAdornment-root .MuiBox-root": {
                        width: 18,
                        height: 18,
                      },
                    }}
                  />

                  {/* <input
                    type="color"
                    disabled={settings?.globalSettings?.fillColor === "transparent"} // Disable when transparent
                    className="form-control"
                    value={
                      settings?.globalSettings?.fillColor || "#000000"
                    }
                    onChange={(e) => handleChange('fillColor', e.target.value)}
                  /> */}
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <label>Font Family:</label>

                  <select
                    id="font"
                    value={settings?.globalSettings?.font || "Arial"}
                    onChange={(e) => handleChange("font", e.target.value)}
                    className="form-select form-control"
                  >
                    {fontOptions}
                  </select>
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <label
                    className="d-flex align-items-center"
                    style={{ height: "25px" }}
                  >
                    Font color: (Transparent
                    <input
                      type="checkbox"
                      className="ml-1 mr-1"
                      checked={
                        settings?.globalSettings?.fontColor === "transparent"
                      }
                      onChange={(e) =>
                        handleChange(
                          "fontColor",
                          e.target.checked ? "transparent" : "#000000",
                        )
                      }
                    />
                    )
                  </label>
                  <MuiColorInput
                    className="form-control"
                    format="hex"
                    value={settings?.globalSettings?.fontColor || "#000000"}
                    onChange={(newHex) =>
                      handleChange("fontColor", newHex?.toUpperCase?.())
                    }
                    fallbackValue="#000000"
                    disabled={
                      settings?.globalSettings?.fontColor === "transparent"
                    } // Disable when transparent
                    // size="small"
                    isAlphaHidden={true} // This disables the alpha slider
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px !important",
                        height: 53,
                      },
                      "& .MuiOutlinedInput-input": {
                        height: "36px",
                      },
                      "& .MuiColorInput-Button": {
                        width: "49px !important", // Increase width
                        height: "40px !important",
                      },
                      "& .MuiInputAdornment-root .MuiBox-root": {
                        width: 18,
                        height: 18,
                      },
                    }}
                  />

                  {/* <input
                    type="color"
                    disabled={settings?.globalSettings?.fontColor === "transparent"} // Disable when transparent
                    value={
                      settings?.globalSettings?.fontColor || "#000000"
                    }
                    onChange={(e) => handleChange('fontColor', e.target.value)}
                    className="form-control"
                  /> */}
                </div>
              </div>

              <div className="col-md-3">
                <div className="form-group">
                  <label>Font Size:</label>
                  <input
                    type="number"
                    value={settings?.globalSettings?.fontSize || ""}
                    onChange={(e) =>
                      handleChange("fontSize", Number(e.target.value))
                    }
                    className="form-control"
                  />
                </div>
              </div>
            </div>

            {/* Shadow Settings */}

            <div className="row menu-card2">
              <div className="col-md-2">
                <div className="form-group">
                  <label>Shadow angle:</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings?.globalSettings?.shadow?.angle ?? 0}
                    onChange={(e) =>
                      handleChange("shadow.angle", Number(e.target.value))
                    }
                  />
                </div>
              </div>

              <div className="col-md-2">
                <div className="form-group">
                  <label>Shadow size:</label>
                  <input
                    type="number"
                    value={settings?.globalSettings?.shadow?.size ?? 0}
                    onChange={(e) =>
                      handleChange("shadow.size", Number(e.target.value))
                    }
                    className="form-control"
                  />
                </div>
              </div>

              <div className="col-md-2">
                <div className="form-group">
                  <label>Shadow distance:</label>
                  <input
                    type="number"
                    value={settings?.globalSettings?.shadow?.distance ?? 0}
                    onChange={(e) =>
                      handleChange("shadow.distance", Number(e.target.value))
                    }
                    className="form-control"
                  />
                </div>
              </div>

              <div className="col-md-2">
                <div className="form-group">
                  <label>Shadow Alpha:</label>
                  <input
                    type="number"
                    value={settings?.globalSettings?.shadow?.alpha ?? 0}
                    onChange={(e) =>
                      handleChange("shadow.alpha", Number(e.target.value))
                    }
                    className="form-control"
                  />
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label
                    className="d-flex align-items-center"
                    style={{ height: "25px" }}
                  >
                    Shadow color: (Transparent
                    <input
                      type="checkbox"
                      className="ml-1 mr-1"
                      checked={
                        settings?.globalSettings?.shadow?.color ===
                        "transparent"
                      }
                      onChange={(e) =>
                        handleChange(
                          "shadow.color",
                          e.target.checked ? "transparent" : "#000000",
                        )
                      }
                    />
                    )
                  </label>

                  <MuiColorInput
                    className="form-control"
                    format="hex"
                    value={settings?.globalSettings?.shadow?.color || "#000000"}
                    onChange={(newHex) =>
                      handleChange("shadow.color", newHex?.toUpperCase?.())
                    }
                    fallbackValue="#000000"
                    disabled={
                      settings?.globalSettings?.shadow?.color === "transparent"
                    } // Disable when transparent
                    // size="small"
                    isAlphaHidden={true} // This disables the alpha slider
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px !important",
                        height: 53,
                      },
                      "& .MuiOutlinedInput-input": {
                        height: "36px",
                      },
                      "& .MuiColorInput-Button": {
                        width: "49px !important", // Increase width
                        height: "40px !important",
                      },
                      "& .MuiInputAdornment-root .MuiBox-root": {
                        width: 18,
                        height: 18,
                      },
                    }}
                  />

                  {/* <input
                    type="color"
                    disabled={settings?.globalSettings?.shadow?.color === "transparent"} // Disable when transparent
                    value={
                      settings?.globalSettings?.shadow?.color || "#000000"
                    }
                    onChange={(e) => handleChange('shadow.color', e.target.value)}
                    className="form-control"
                  /> */}
                </div>
              </div>
            </div>

            {/* Highlight */}

            <div className="row menu-card2">
              <div className="col-md-4">
                <div className="form-group">
                  <label
                    className="d-flex align-items-center"
                    style={{ height: "25px" }}
                  >
                    Highlight boarder color: (Transparent
                    <input
                      type="checkbox"
                      className="ml-1 mr-1"
                      checked={
                        settings?.globalSettings?.highlight?.borderColor ===
                        "transparent"
                      }
                      onChange={(e) =>
                        handleChange(
                          "highlight.borderColor",
                          e.target.checked ? "transparent" : "#000000",
                        )
                      }
                    />
                    )
                  </label>
                  <MuiColorInput
                    className="form-control"
                    format="hex"
                    value={
                      settings?.globalSettings?.highlight?.borderColor ||
                      "#000000"
                    }
                    onChange={(newHex) =>
                      handleChange(
                        "highlight.borderColor",
                        newHex?.toUpperCase?.(),
                      )
                    }
                    fallbackValue="#000000"
                    disabled={
                      settings?.globalSettings?.highlight?.borderColor ===
                      "transparent"
                    } // Disable when transparent
                    // size="small"
                    isAlphaHidden={true} // This disables the alpha slider
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px !important",
                        height: 53,
                      },
                      "& .MuiOutlinedInput-input": {
                        height: "36px",
                      },
                      "& .MuiColorInput-Button": {
                        width: "49px !important", // Increase width
                        height: "40px !important",
                      },
                      "& .MuiInputAdornment-root .MuiBox-root": {
                        width: 18,
                        height: 18,
                      },
                    }}
                  />

                  {/* <input
                    type="color"
                    className="form-control"
                    disabled={settings?.globalSettings?.highlight?.borderColor === "transparent"} // Disable when transparent
                    value={
                      settings?.globalSettings?.highlight?.borderColor || "#000000"
                    }
                    onChange={(e) => handleChange('highlight.borderColor', e.target.value)}
                  /> */}
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label
                    className="d-flex align-items-center"
                    style={{ height: "25px" }}
                  >
                    Highlight fill color: (Transparent
                    <input
                      type="checkbox"
                      className="ml-1 mr-1"
                      checked={
                        settings?.globalSettings?.highlight?.fillColor ===
                        "transparent"
                      }
                      onChange={(e) =>
                        handleChange(
                          "highlight.fillColor",
                          e.target.checked ? "transparent" : "#000000",
                        )
                      }
                    />
                    )
                  </label>

                  <MuiColorInput
                    className="form-control"
                    format="hex"
                    value={
                      settings?.globalSettings?.highlight?.fillColor ||
                      "#000000"
                    }
                    onChange={(newHex) =>
                      handleChange(
                        "highlight.fillColor",
                        newHex?.toUpperCase?.(),
                      )
                    }
                    fallbackValue="#000000"
                    disabled={
                      settings?.globalSettings?.highlight?.fillColor ===
                      "transparent"
                    } // Disable when transparent
                    // size="small"
                    isAlphaHidden={true} // This disables the alpha slider
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px !important",
                        height: 53,
                      },
                      "& .MuiOutlinedInput-input": {
                        height: "36px",
                      },
                      "& .MuiColorInput-Button": {
                        width: "49px !important", // Increase width
                        height: "40px !important",
                      },
                      "& .MuiInputAdornment-root .MuiBox-root": {
                        width: 18,
                        height: 18,
                      },
                    }}
                  />

                  {/* <input
                    type="color"
                    disabled={settings?.globalSettings?.highlight?.fillColor === "transparent"} // Disable when transparent
                    className="form-control"
                    value={
                      settings?.globalSettings?.highlight?.fillColor || "#000000"
                    }
                    onChange={(e) => handleChange('highlight.fillColor', e.target.value)}
                  /> */}
                </div>
              </div>

              <div className="col-md-4">
                <div className="form-group">
                  <label
                    className="d-flex align-items-center"
                    style={{ height: "25px" }}
                  >
                    Highlight Font color: (Transparent
                    <input
                      type="checkbox"
                      className="ml-1 mr-1"
                      checked={
                        settings?.globalSettings?.highlight?.fontColor ===
                        "transparent"
                      }
                      onChange={(e) =>
                        handleChange(
                          "highlight.fontColor",
                          e.target.checked ? "transparent" : "#000000",
                        )
                      }
                    />
                    )
                  </label>

                  <MuiColorInput
                    className="form-control"
                    format="hex"
                    value={
                      settings?.globalSettings?.highlight?.fontColor ||
                      "#000000"
                    }
                    onChange={(newHex) =>
                      handleChange(
                        "highlight.fontColor",
                        newHex?.toUpperCase?.(),
                      )
                    }
                    fallbackValue="#000000"
                    disabled={
                      settings?.globalSettings?.highlight?.fontColor ===
                      "transparent"
                    } // Disable when transparent
                    // size="small"
                    isAlphaHidden={true} // This disables the alpha slider
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "0px !important",
                        height: 53,
                      },
                      "& .MuiOutlinedInput-input": {
                        height: "36px",
                      },
                      "& .MuiColorInput-Button": {
                        width: "49px !important", // Increase width
                        height: "40px !important",
                      },
                      "& .MuiInputAdornment-root .MuiBox-root": {
                        width: 18,
                        height: 18,
                      },
                    }}
                  />

                  {/* <input
                    type="color"
                    disabled={settings?.globalSettings?.highlight?.fontColor === "transparent"} // Disable when transparent
                    className="form-control"
                    value={
                      settings?.globalSettings?.highlight?.fontColor || "#000000"
                    }
                    onChange={(e) => handleChange('highlight.fontColor', e.target.value)}
                  /> */}
                </div>
              </div>
            </div>

            {/* click Sound */}

            <div className="row menu-card2">
              <div className="col-md-6">
                <div className="form-group">
                  <label>Upload Sounder Picker File:</label>
                  <br></br>
                  <input type="file" onChange={handleFileChange} />
                  {file ? (
                    <button
                      type="button"
                      style={{ backgroundColor: buttonColor }}
                      onClick={handleUpload}
                    >
                      Upload
                    </button>
                  ) : (
                    ""
                  )}

                  {/* {uploadedFileInfo && ( */}
                  <div>
                    {/* <h5>Uploaded File Info:</h5>
                  <p><strong>Filename:</strong> {uploadedFileInfo.filename}</p>
                  {uploadedFileInfo.type.startsWith('audio/') && (
                    <audio controls>
                      <source src={uploadedFileInfo.url} type={uploadedFileInfo.type} />
                      Your browser does not support the audio element.
                    </audio>
                  )} */}

                    <a
                      href={settings?.globalSettings?.clickSound}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {settings?.globalSettings?.clickSoundFilename}
                    </a>
                  </div>
                  {/* // )} */}
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>
                    Click sound volume{" "}
                    <strong>(The volume range should be 0 to 1)</strong>:
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings?.globalSettings?.clickSoundVolume ?? 0}
                    min="0"
                    max="1"
                    step="0.1"
                    onChange={(e) => {
                      let value = Number(e.target.value);
                      if (value < 0) value = 0;
                      if (value > 1) value = 1;
                      handleChange("clickSoundVolume", value);
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Next Button Upload */}
            <div className="row menu-card2">
              <div className="col-md-6">
                <div className="form-group">
                  <label>Upload Next Navigation Button Image:</label>
                  <br></br>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleNextButtonFileChange}
                  />
                  {nextButtonFile ? (
                    <button
                      type="button"
                      style={{ backgroundColor: buttonColor }}
                      onClick={handleNextButtonUpload}
                    >
                      Upload
                    </button>
                  ) : (
                    ""
                  )}
                  {nextButtonUploadStatus && (
                    <p style={{ color: "green", marginTop: "10px" }}>
                      {nextButtonUploadStatus}
                    </p>
                  )}
                  <div>
                    {settings?.globalSettings?.nextButtonFileUrl && (
                      <div style={{ marginTop: "10px" }}>
                        <img
                          src={settings?.globalSettings?.nextButtonFileUrl}
                          alt="Next Button"
                          style={{ maxWidth: "150px", maxHeight: "150px" }}
                        />
                        <p>
                          <a
                            href={settings?.globalSettings?.nextButtonFileUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {settings?.globalSettings?.nextButtonFileName}
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label>Next Button Width (%):</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings?.globalSettings?.nextButtonWidth ?? 0}
                    min="0"
                    max="100"
                    onChange={(e) => {
                      let value = Number(e.target.value);
                      if (value < 0) value = 0;
                      if (value > 100) value = 100;
                      handleChange("nextButtonWidth", value);
                    }}
                  />
                </div>
              </div>
            </div>

            <button style={{ backgroundColor: buttonColor }} type="submit">
              Save Settings
            </button>
          </form>
        </div>
      </div>
      <style jsx>{`
        .btn-back-effect {
          border: 1px solid #6c757d !important;
          color: #6c757d !important;
          background-color: transparent !important;
          border-radius: 8px !important;
        }

        .btn-back-effect:hover {
          background-color: var(--brand-color) !important;
          color: #fff !important;
          border-color: var(--brand-color) !important;
        }
      `}</style>
    </div>
  );
}
